import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router';
import { useWallet } from '@solana/wallet-adapter-react';
import type { LeaderboardEntry, LeaderboardResponse, ApiResponse } from '@beat-games/shared-types';
import type { PhaseType } from '@beat-games/game-config';
import { WalletAvatar, WalletAvatarSmall } from '../components/ui/WalletAvatar';

const API_BASE = import.meta.env.VITE_API_URL || '';

// Extended entry type with qualification status and replay info
interface ExtendedLeaderboardEntry extends LeaderboardEntry {
  qualificationStatus?: 'qualified' | 'eliminated' | 'pending';
  qualifiedForPhase?: string;
  bestSessionId?: string;
  hasReplay?: boolean;
}

// Extended response type
interface ExtendedLeaderboardResponse extends LeaderboardResponse {
  entries: ExtendedLeaderboardEntry[];
  currentPhaseType: string;
  phaseProcessed: boolean;
}

const PHASE_OPTIONS: { value: PhaseType; label: string; short: string }[] = [
  { value: 'QUALIFIER', label: 'Qualifiers', short: 'QUA' },
  { value: 'SHORTLIST', label: 'Shortlist', short: 'SHT' },
  { value: 'SEMIFINAL', label: 'Semifinals', short: 'SEM' },
  { value: 'FINAL', label: 'Finals', short: 'FIN' },
];

export default function Leaderboard() {
  const { publicKey } = useWallet();
  const [entries, setEntries] = useState<ExtendedLeaderboardEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [qualificationLine, setQualificationLine] = useState(256);
  const [seasonPhase, setSeasonPhase] = useState(1);
  const [phaseFilter, setPhaseFilter] = useState<PhaseType>('QUALIFIER');
  const [userRank, setUserRank] = useState<{ rank: number | null; score: number | null }>({ rank: null, score: null });
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [phaseProcessed, setPhaseProcessed] = useState(false);
  const [hoveredRank, setHoveredRank] = useState<number | null>(null);

  // AbortController refs for cancelling in-flight requests
  const leaderboardAbortRef = useRef<AbortController | null>(null);
  const rankAbortRef = useRef<AbortController | null>(null);

  const LIMIT = 50;

  const fetchLeaderboard = useCallback(async (pageNum: number, append = false, phase: PhaseType = phaseFilter) => {
    // Cancel any in-flight request
    leaderboardAbortRef.current?.abort();
    leaderboardAbortRef.current = new AbortController();

    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }
    setError(null);

    try {
      const response = await fetch(
        `${API_BASE}/api/leaderboard?page=${pageNum}&limit=${LIMIT}&phase=${phase}`,
        { signal: leaderboardAbortRef.current.signal }
      );
      const data: ApiResponse<ExtendedLeaderboardResponse> = await response.json();

      if (data.success && data.data) {
        if (append) {
          setEntries(prev => [...prev, ...data.data!.entries]);
        } else {
          setEntries(data.data.entries);
        }
        setTotalEntries(data.data.totalEntries);
        setQualificationLine(data.data.qualificationLine);
        setSeasonPhase(data.data.seasonPhase);
        setPhaseProcessed(data.data.phaseProcessed || false);
      } else {
        throw new Error(data.error?.message || 'Failed to fetch leaderboard');
      }
    } catch (err) {
      // Ignore aborted requests
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      console.error('Failed to fetch leaderboard:', err);
      setError(append ? 'Failed to load more entries' : 'Failed to load leaderboard');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [phaseFilter]);

  const fetchUserRank = useCallback(async (phase: PhaseType = phaseFilter) => {
    if (!publicKey) {
      setUserRank({ rank: null, score: null });
      return;
    }

    // Cancel any in-flight request
    rankAbortRef.current?.abort();
    rankAbortRef.current = new AbortController();

    try {
      const response = await fetch(
        `${API_BASE}/api/leaderboard/rank/${publicKey.toBase58()}?phase=${phase}`,
        { signal: rankAbortRef.current.signal }
      );
      const data = await response.json();

      if (data.success && data.data) {
        setUserRank({ rank: data.data.rank, score: data.data.score });
      }
    } catch (err) {
      // Ignore aborted requests
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      console.error('Failed to fetch user rank:', err);
      setUserRank({ rank: null, score: null });
    }
  }, [publicKey, phaseFilter]);

  // Fetch current phase on mount and set it as the default filter
  const [initialPhaseLoaded, setInitialPhaseLoaded] = useState(false);

  useEffect(() => {
    async function fetchCurrentPhase() {
      try {
        const response = await fetch(`${API_BASE}/api/season/status`);
        const data = await response.json();
        if (data.success && data.data?.phaseType) {
          const currentPhaseType = data.data.phaseType as PhaseType;
          // Only set if it's a valid playable phase
          if (['QUALIFIER', 'SHORTLIST', 'SEMIFINAL', 'FINAL'].includes(currentPhaseType)) {
            setPhaseFilter(currentPhaseType);
          }
        }
      } catch (err) {
        console.error('Failed to fetch current phase:', err);
      } finally {
        setInitialPhaseLoaded(true);
      }
    }
    fetchCurrentPhase();
  }, []);

  useEffect(() => {
    if (initialPhaseLoaded) {
      fetchLeaderboard(1);
    }
  }, [fetchLeaderboard, initialPhaseLoaded]);

  useEffect(() => {
    if (initialPhaseLoaded) {
      fetchUserRank();
    }
  }, [fetchUserRank, initialPhaseLoaded]);

  // Cleanup: abort pending requests on unmount
  useEffect(() => {
    return () => {
      leaderboardAbortRef.current?.abort();
      rankAbortRef.current?.abort();
    };
  }, []);

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchLeaderboard(nextPage, true);
  };

  const hasMore = entries.length < totalEntries;

  const formatWallet = (address: string) =>
    address.includes('...') ? address : `${address.slice(0, 4)}...${address.slice(-4)}`;

  // Format survival time as seconds
  const formatTime = (ms: number) => (ms / 1000).toFixed(3) + 's';

  // Format score as points
  const formatPoints = (score: number) => score.toLocaleString();

  // Format precision penalty (negative value)
  const formatPrecisionPenalty = (precisionError: number) => {
    const penalty = Math.floor(precisionError * 0.8);
    return penalty > 0 ? `-${penalty}` : '0';
  };

  const getPhaseLabel = (phase: number) => {
    const phases = ['Qualifiers', 'Shortlist', 'Semifinals', 'Finals'];
    return phases[phase - 1] || 'Qualifiers';
  };

  // Check if selected phase is playable (current or past phase)
  const getPhaseNumber = (phase: PhaseType): number => {
    const phaseMap: Record<PhaseType, number> = {
      QUALIFIER: 1,
      SHORTLIST: 2,
      SEMIFINAL: 3,
      FINAL: 4,
      CLOSED: 5,
    };
    return phaseMap[phase];
  };

  const isPhasePlayable = getPhaseNumber(phaseFilter) === seasonPhase;

  // Get top 3 for podium display
  const top3 = entries.slice(0, 3);

  return (
    <div className="min-h-screen bg-[#030303] text-white overflow-x-hidden">
      {/* Grain overlay */}
      <div className="grain-overlay" />

      {/* Background */}
      <div className="fixed inset-0 pointer-events-none">
        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `
              linear-gradient(to right, white 1px, transparent 1px),
              linear-gradient(to bottom, white 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px',
          }}
        />
        {/* Radial spotlight */}
        <div
          className="absolute inset-0 opacity-40"
          style={{
            background: 'radial-gradient(ellipse 60% 40% at 50% 0%, rgba(255,255,255,0.03) 0%, transparent 70%)',
          }}
        />
      </div>

      <div className="relative z-10">
        {/* Hero Header */}
        <header className="relative pt-12 pb-8 px-4">
          <div className="max-w-6xl mx-auto">
            {/* Top bar */}
            <div className="flex items-center justify-between mb-8 animate-slide-up-fade">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#22c55e] opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#22c55e]"></span>
                  </span>
                  <span className="font-mono text-[10px] tracking-[0.2em] text-[#22c55e]/80">LIVE</span>
                </div>
                <span className="text-white/20">|</span>
                <span className="font-mono text-xs text-white/40 tracking-wider">
                  SEASON 1 · {getPhaseLabel(seasonPhase).toUpperCase()}
                </span>
              </div>

              <div className="flex items-center gap-3">
                <span className="font-mono text-[10px] text-white/30 tracking-wider">
                  {totalEntries.toLocaleString()} PLAYERS
                </span>
              </div>
            </div>

            {/* Main title */}
            <div className="text-center mb-8 animate-slide-up-fade" style={{ animationDelay: '0.05s' }}>
              <h1 className="font-display text-[clamp(3rem,10vw,8rem)] leading-[0.9] tracking-[0.02em] mb-4">
                <span className="text-transparent bg-clip-text bg-gradient-to-b from-white via-white to-white/40">
                  LEADERBOARD
                </span>
              </h1>
              <p className="font-mono text-sm text-white/40">
                Top <span className="text-[#22c55e] font-semibold">{qualificationLine}</span> advance to next phase
              </p>
            </div>

            {/* Phase filter tabs */}
            <div className="flex justify-center mb-8 animate-slide-up-fade" style={{ animationDelay: '0.1s' }}>
              <div className="inline-flex flex-wrap justify-center gap-1 sm:gap-0 sm:flex-nowrap bg-white/[0.02] border border-white/[0.06] p-1.5 sm:p-1">
                {PHASE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      setPhaseFilter(option.value);
                      setPage(1);
                      setEntries([]);
                      fetchLeaderboard(1, false, option.value);
                      fetchUserRank(option.value);
                    }}
                    className={`min-w-[44px] min-h-[44px] px-4 py-3 font-mono text-xs tracking-wider transition-all ${
                      phaseFilter === option.value
                        ? 'bg-white text-black'
                        : 'text-white/40 hover:text-white/70'
                    }`}
                  >
                    <span className="hidden sm:inline">{option.label}</span>
                    <span className="sm:hidden">{option.short}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </header>

        {/* User Stats Bar */}
        {(userRank.rank || publicKey) && (
          <div className="border-y border-white/[0.06] bg-white/[0.01] animate-slide-up-fade" style={{ animationDelay: '0.15s' }}>
            <div className="max-w-6xl mx-auto px-4 py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-8">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden">
                      {publicKey ? (
                        <WalletAvatar address={publicKey.toBase58()} size={48} />
                      ) : (
                        <span className="font-mono text-lg text-white/60">??</span>
                      )}
                    </div>
                    <div>
                      <div className="font-mono text-[10px] text-white/30 tracking-wider mb-1">YOUR POSITION</div>
                      <div className="flex items-center gap-3">
                        <span className={`font-display text-3xl ${
                          userRank.rank
                            ? userRank.rank <= qualificationLine
                              ? 'text-[#22c55e]'
                              : 'text-[#ef4444]'
                            : 'text-white/20'
                        }`}>
                          {userRank.rank ? `#${userRank.rank}` : '---'}
                        </span>
                        {userRank.rank && userRank.rank <= qualificationLine && (
                          <span className="px-2 py-0.5 bg-[#22c55e]/10 border border-[#22c55e]/20 font-mono text-[9px] text-[#22c55e] tracking-wider">
                            QUALIFYING
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="hidden sm:block w-px h-12 bg-white/10" />

                  <div className="hidden sm:block">
                    <div className="font-mono text-[10px] text-white/30 tracking-wider mb-1">BEST SCORE</div>
                    <span className="font-mono text-2xl text-white/70">
                      {userRank.score ? formatPoints(userRank.score) + ' pts' : '--- pts'}
                    </span>
                  </div>

                  <div className="hidden md:block w-px h-12 bg-white/10" />

                  <div className="hidden md:block">
                    <div className="font-mono text-[10px] text-white/30 tracking-wider mb-1">TO QUALIFY</div>
                    <span className="font-mono text-lg text-white/40">
                      {userRank.rank
                        ? userRank.rank <= qualificationLine
                          ? 'Secured'
                          : `+${userRank.rank - qualificationLine} spots`
                        : '---'}
                    </span>
                  </div>
                </div>

                {isPhasePlayable && (
                  <Link
                    to="/play"
                    className="group flex items-center gap-3 px-6 py-3 bg-white text-black font-bold text-sm tracking-wider transition-all hover:shadow-[0_0_40px_rgba(255,255,255,0.3)]"
                  >
                    <span>{userRank.rank ? 'IMPROVE' : 'PLAY'}</span>
                    <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="max-w-6xl mx-auto px-4 py-12">
          {isLoading ? (
            <div className="text-center py-24 animate-slide-up-fade">
              <div className="relative w-16 h-16 mx-auto mb-6">
                <div className="absolute inset-0 border-2 border-white/10 rounded-full" />
                <div className="absolute inset-0 border-2 border-transparent border-t-white rounded-full animate-spin" />
              </div>
              <p className="font-mono text-sm text-white/40 tracking-wider">LOADING RANKINGS</p>
            </div>
          ) : error && entries.length === 0 ? (
            <div className="text-center py-24 animate-slide-up-fade">
              <div className="w-16 h-16 mx-auto mb-6 border border-[#ef4444]/20 bg-[#ef4444]/5 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-[#ef4444]/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <p className="text-white/50 mb-6">{error}</p>
              <button
                onClick={() => fetchLeaderboard(1)}
                className="px-8 py-3 border border-white/20 text-white/70 font-mono text-sm tracking-wider hover:border-white/40 hover:text-white transition-all"
              >
                RETRY
              </button>
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-24 animate-slide-up-fade">
              <div className="w-20 h-20 mx-auto mb-6 border border-white/10 bg-white/[0.02] rounded-full flex items-center justify-center">
                <img src="/images/bg-logo.svg" alt="" className="w-10 h-10 opacity-30" />
              </div>
              <h3 className="font-display text-2xl mb-2">No Scores Yet</h3>
              <p className="text-white/40 mb-8">
                {isPhasePlayable ? 'Be the first to set a time' : 'This phase has not started yet'}
              </p>
              {isPhasePlayable && (
                <Link
                  to="/play"
                  className="inline-flex items-center gap-3 px-8 py-4 bg-white text-black font-bold tracking-wider"
                >
                  <span>CLAIM #1</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              )}
            </div>
          ) : (
            <>
              {/* Podium - Top 3 */}
              {top3.length >= 3 && (
                <div className="mb-16 animate-slide-up-fade" style={{ animationDelay: '0.2s' }}>
                  <div className="flex items-center gap-3 mb-8">
                    <span className="font-mono text-[10px] text-white/30 tracking-[0.2em]">TOP PERFORMERS</span>
                    <div className="flex-1 h-px bg-white/10" />
                  </div>

                  <div className="grid grid-cols-3 gap-4 max-w-4xl mx-auto">
                    {/* 2nd Place */}
                    <div className="order-1 pt-8">
                      <div
                        className="group relative p-6 border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/20 transition-all"
                        onMouseEnter={() => setHoveredRank(2)}
                        onMouseLeave={() => setHoveredRank(null)}
                      >
                        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                          <div className="w-8 h-8 bg-[#9ca3af] rounded-full flex items-center justify-center font-display text-lg text-black">
                            2
                          </div>
                        </div>
                        <div className="text-center pt-4">
                          <div className="font-mono text-[10px] text-white/30 tracking-wider mb-2">SILVER</div>
                          <div className="flex justify-center mb-2">
                            <WalletAvatar address={top3[1]!.walletAddress} size={40} className="border border-[#9ca3af]/30" />
                          </div>
                          <Link
                            to={`/profile/${top3[1]!.walletAddress}`}
                            className="font-mono text-sm text-white/80 hover:text-white transition-colors block mb-3"
                          >
                            {formatWallet(top3[1]!.walletAddress)}
                          </Link>
                          <div className="font-mono text-2xl text-[#9ca3af]">
                            {formatPoints(top3[1]!.score)} <span className="text-sm">pts</span>
                          </div>
                          <div className="font-mono text-[10px] text-white/30 mt-1">
                            {formatTime(top3[1]!.survivalTimeMs)} <span className="text-[#ef4444]/60">{formatPrecisionPenalty(top3[1]!.precisionError)}</span>
                          </div>
                        </div>
                        {/* Shine effect on hover */}
                        <div className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent transition-opacity ${hoveredRank === 2 ? 'opacity-100' : 'opacity-0'}`} />
                      </div>
                    </div>

                    {/* 1st Place */}
                    <div className="order-2">
                      <div
                        className="group relative p-8 border border-[#fbbf24]/30 bg-gradient-to-b from-[#fbbf24]/10 to-transparent hover:border-[#fbbf24]/50 transition-all"
                        onMouseEnter={() => setHoveredRank(1)}
                        onMouseLeave={() => setHoveredRank(null)}
                      >
                        <div className="absolute -top-5 left-1/2 -translate-x-1/2">
                          <div className="relative">
                            <div className="w-10 h-10 bg-gradient-to-b from-[#fbbf24] to-[#d97706] rounded-full flex items-center justify-center font-display text-xl text-black shadow-[0_0_30px_rgba(251,191,36,0.4)]">
                              1
                            </div>
                            <img src="/images/badges/champion.png" alt="" className="absolute -top-2 -right-2 w-5 h-5" />
                          </div>
                        </div>
                        <div className="text-center pt-6">
                          <div className="font-mono text-[10px] text-[#fbbf24]/60 tracking-wider mb-2">CHAMPION</div>
                          <div className="flex justify-center mb-2">
                            <WalletAvatar address={top3[0]!.walletAddress} size={48} className="border-2 border-[#fbbf24]/40 shadow-[0_0_20px_rgba(251,191,36,0.3)]" />
                          </div>
                          <Link
                            to={`/profile/${top3[0]!.walletAddress}`}
                            className="font-mono text-sm text-white hover:text-[#fbbf24] transition-colors block mb-3"
                          >
                            {formatWallet(top3[0]!.walletAddress)}
                          </Link>
                          <div className="font-mono text-3xl text-[#fbbf24] text-glow">
                            {formatPoints(top3[0]!.score)} <span className="text-lg">pts</span>
                          </div>
                          <div className="font-mono text-[10px] text-white/40 mt-1">
                            {formatTime(top3[0]!.survivalTimeMs)} <span className="text-[#ef4444]/60">{formatPrecisionPenalty(top3[0]!.precisionError)}</span>
                          </div>
                        </div>
                        {/* Crown glow effect */}
                        <div className="absolute -inset-px bg-gradient-to-b from-[#fbbf24]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-sm pointer-events-none" />
                      </div>
                    </div>

                    {/* 3rd Place */}
                    <div className="order-3 pt-12">
                      <div
                        className="group relative p-6 border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/20 transition-all"
                        onMouseEnter={() => setHoveredRank(3)}
                        onMouseLeave={() => setHoveredRank(null)}
                      >
                        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                          <div className="w-8 h-8 bg-[#cd7f32] rounded-full flex items-center justify-center font-display text-lg text-black">
                            3
                          </div>
                        </div>
                        <div className="text-center pt-4">
                          <div className="font-mono text-[10px] text-white/30 tracking-wider mb-2">BRONZE</div>
                          <div className="flex justify-center mb-2">
                            <WalletAvatar address={top3[2]!.walletAddress} size={40} className="border border-[#cd7f32]/30" />
                          </div>
                          <Link
                            to={`/profile/${top3[2]!.walletAddress}`}
                            className="font-mono text-sm text-white/80 hover:text-white transition-colors block mb-3"
                          >
                            {formatWallet(top3[2]!.walletAddress)}
                          </Link>
                          <div className="font-mono text-2xl text-[#cd7f32]">
                            {formatPoints(top3[2]!.score)} <span className="text-sm">pts</span>
                          </div>
                          <div className="font-mono text-[10px] text-white/30 mt-1">
                            {formatTime(top3[2]!.survivalTimeMs)} <span className="text-[#ef4444]/60">{formatPrecisionPenalty(top3[2]!.precisionError)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Rankings Table */}
              <div className="animate-slide-up-fade" style={{ animationDelay: '0.25s' }}>
                <div className="flex items-center gap-3 mb-6">
                  <span className="font-mono text-[10px] text-white/30 tracking-[0.2em]">ALL RANKINGS</span>
                  <div className="flex-1 h-px bg-white/10" />
                  <span className="font-mono text-[10px] text-white/20">
                    {entries.length} of {totalEntries}
                  </span>
                </div>

                {/* Table header */}
                <div className="grid grid-cols-12 gap-4 px-4 py-3 border-y border-white/[0.06] bg-white/[0.01]">
                  <div className="col-span-1 font-mono text-[10px] text-white/30 tracking-wider">RANK</div>
                  <div className="col-span-4 font-mono text-[10px] text-white/30 tracking-wider">PLAYER</div>
                  <div className="col-span-3 font-mono text-[10px] text-white/30 tracking-wider text-right">SCORE</div>
                  <div className="col-span-2 font-mono text-[10px] text-white/30 tracking-wider text-right">STATUS</div>
                  <div className="col-span-2 font-mono text-[10px] text-white/30 tracking-wider text-right">REPLAY</div>
                </div>

                {/* Table rows */}
                <div>
                  {entries.map((entry) => {
                    const isWithinQualificationLine = entry.rank <= qualificationLine;
                    const isQualificationBoundary = entry.rank === qualificationLine;
                    const isJustBelowLine = entry.rank === qualificationLine + 1;

                    // Determine qualification status
                    const showQualificationStatus = phaseProcessed && entry.qualificationStatus;
                    const isConfirmedQualified = showQualificationStatus && entry.qualificationStatus === 'qualified';
                    const isEliminated = showQualificationStatus && entry.qualificationStatus === 'eliminated';
                    const isLikelyToQualify = !phaseProcessed && isWithinQualificationLine;

                    const isCurrentUser = publicKey && entry.walletAddress === publicKey.toBase58();

                    return (
                      <div key={`${entry.walletAddress}-${entry.rank}`}>
                        {/* Qualification cutoff line */}
                        {isJustBelowLine && !phaseProcessed && (
                          <div className="relative py-3">
                            <div className="absolute inset-x-0 top-1/2 h-px bg-gradient-to-r from-transparent via-[#ef4444]/50 to-transparent" />
                            <div className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 px-3 py-1 bg-[#030303] border border-[#ef4444]/30">
                              <span className="font-mono text-[9px] text-[#ef4444]/70 tracking-wider">
                                QUALIFICATION CUTOFF
                              </span>
                            </div>
                          </div>
                        )}

                        <div
                          className={`group grid grid-cols-12 gap-4 px-4 py-4 border-b border-white/[0.03] transition-all hover:bg-white/[0.03] ${
                            isCurrentUser ? 'bg-white/[0.04] border-l-2 border-l-white/50' : ''
                          } ${isEliminated ? 'opacity-50' : ''} ${
                            isQualificationBoundary && !phaseProcessed ? 'border-b-[#22c55e]/30 border-b-2' : ''
                          }`}
                        >
                          {/* Rank */}
                          <div className="col-span-1 flex items-center">
                            <span className={`font-display text-xl tabular-nums ${
                              entry.rank <= 3
                                ? entry.rank === 1
                                  ? 'text-[#fbbf24]'
                                  : entry.rank === 2
                                  ? 'text-[#9ca3af]'
                                  : 'text-[#cd7f32]'
                                : isWithinQualificationLine
                                ? 'text-white/80'
                                : 'text-white/30'
                            }`}>
                              {entry.rank}
                            </span>
                          </div>

                          {/* Player */}
                          <div className="col-span-4 flex items-center gap-3">
                            <WalletAvatarSmall address={entry.walletAddress} className="flex-shrink-0" />
                            <Link
                              to={`/profile/${entry.walletAddress}`}
                              className={`font-mono text-sm transition-colors ${
                                isCurrentUser ? 'text-white' : 'text-white/70 hover:text-white'
                              }`}
                            >
                              {formatWallet(entry.walletAddress)}
                            </Link>

                            {isCurrentUser && (
                              <span className="px-1.5 py-0.5 bg-white/10 font-mono text-[9px] text-white/70 tracking-wider">
                                YOU
                              </span>
                            )}

                            {entry.badges && entry.badges.length > 0 && (
                              <div className="flex gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                                {entry.badges.slice(0, 2).map((badge) => (
                                  <div
                                    key={badge}
                                    className="w-4 h-4 bg-white/10 rounded flex items-center justify-center"
                                    title={badge}
                                  >
                                    <span className="text-[8px]">
                                      {badge === 'perfect' ? '★' : badge === 'champion' ? '♛' : '●'}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Score breakdown: survival time, precision penalty, final score */}
                          <div className="col-span-3 flex items-center justify-end">
                            <div className="text-right">
                              <div className={`font-mono text-sm tabular-nums ${
                                isConfirmedQualified || isLikelyToQualify ? 'text-white' : 'text-white/60'
                              }`}>
                                {formatPoints(entry.score)} <span className="text-white/30 text-[10px]">pts</span>
                              </div>
                              <div className="font-mono text-[10px] text-white/30 tabular-nums">
                                {formatTime(entry.survivalTimeMs)} <span className="text-[#ef4444]/60">{formatPrecisionPenalty(entry.precisionError)}</span>
                              </div>
                            </div>
                          </div>

                          {/* Status */}
                          <div className="col-span-2 flex items-center justify-end">
                            {showQualificationStatus ? (
                              <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-[9px] font-mono tracking-wider ${
                                isConfirmedQualified
                                  ? 'bg-[#22c55e]/10 text-[#22c55e] border border-[#22c55e]/20'
                                  : 'bg-[#ef4444]/10 text-[#ef4444] border border-[#ef4444]/20'
                              }`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${
                                  isConfirmedQualified ? 'bg-[#22c55e]' : 'bg-[#ef4444]'
                                }`} />
                                {isConfirmedQualified ? 'QUALIFIED' : 'OUT'}
                              </span>
                            ) : isLikelyToQualify ? (
                              <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-[#22c55e]/5 text-[9px] font-mono tracking-wider text-[#22c55e]/70 border border-[#22c55e]/10">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#22c55e]/50 animate-pulse" />
                                IN
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-white/[0.02] text-[9px] font-mono tracking-wider text-white/30 border border-white/5">
                                <span className="w-1.5 h-1.5 rounded-full bg-white/20" />
                                {entry.rank - qualificationLine > 0 ? `+${entry.rank - qualificationLine}` : '---'}
                              </span>
                            )}
                          </div>

                          {/* Replay */}
                          <div className="col-span-2 flex items-center justify-end">
                            {entry.hasReplay && entry.bestSessionId ? (
                              <Link
                                to={`/replay/${entry.bestSessionId}`}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all text-white/50 hover:text-white text-[10px] font-mono tracking-wider"
                              >
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M8 5.14v14.72a1 1 0 001.5.86l11-7.36a1 1 0 000-1.72l-11-7.36a1 1 0 00-1.5.86z" />
                                </svg>
                                <span className="hidden sm:inline">WATCH</span>
                              </Link>
                            ) : (
                              <span className="text-white/20 text-[10px] font-mono">-</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Load more */}
                {hasMore && (
                  <div className="py-8 text-center">
                    <button
                      onClick={loadMore}
                      disabled={isLoadingMore}
                      className="group inline-flex items-center gap-3 px-8 py-3 border border-white/10 text-white/50 font-mono text-sm tracking-wider hover:border-white/20 hover:text-white/80 transition-all disabled:opacity-50"
                    >
                      {isLoadingMore ? (
                        <>
                          <div className="w-4 h-4 border border-white/20 border-t-white/60 rounded-full animate-spin" />
                          <span>LOADING</span>
                        </>
                      ) : (
                        <>
                          <span>LOAD MORE</span>
                          <svg className="w-4 h-4 transition-transform group-hover:translate-y-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                          </svg>
                        </>
                      )}
                    </button>
                    <p className="mt-3 font-mono text-[10px] text-white/20">
                      Showing {entries.length} of {totalEntries} players
                    </p>
                  </div>
                )}
              </div>

              {/* Legend - contextual based on phase status */}
              <div className="mt-12 pt-8 border-t border-white/[0.06]">
                <div className="flex flex-wrap items-center justify-center gap-6 text-xs">
                  {phaseProcessed ? (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-[#22c55e]" />
                        <span className="font-mono text-white/40">Qualified</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-[#ef4444]" />
                        <span className="font-mono text-white/40">Eliminated</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-[#22c55e]" />
                        <span className="font-mono text-white/40">In</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-white/30" />
                        <span className="font-mono text-white/40">Below Cutoff</span>
                      </div>
                    </>
                  )}
                </div>
                {!phaseProcessed && (
                  <p className="mt-4 text-center font-mono text-[10px] text-white/20">
                    Final qualification confirmed when phase ends
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
